import * as React from 'react';

import { DropZone, isFileDropItem, Checkbox } from '@rematter/ui-web';
import { useToast } from '@rematter/ui-web/hooks';
import { cn } from '@rematter/ui-web/lib';
import { useAtom, useAtomValue, useSetAtom } from 'jotai';
import {
  File,
  FileText,
  Folder,
  Image,
  PackageOpen,
  UploadIcon,
} from 'lucide-react';
import { navigate } from 'wouter/use-browser-location';

import { $uploadFilesToCurrentDirectory } from '../../_state/fileActions.state';
import {
  $files,
  $selectSingleFile,
  $toggleFileSelection,
  $selectedFileIds,
  $currentFolderId,
  $clearSelection,
  $isSelectionMode,
  $searchTerm,
} from '../../_state/files.state';
import { useTranslations } from '../../i18n/useTranslations';
import { FileRecord } from '../../types/files';

import { FileActionMenu } from './FileActionMenu';
import { FileIndicators } from './FileIndicators';

interface FileGridProps {
  viewMode?: 'explorer' | 'shared';
}

export const FileGrid = (_props: FileGridProps) => {
  const toast = useToast();
  const t = useTranslations();

  const files = useAtomValue($files);
  const selectedFileIds = useAtomValue($selectedFileIds);
  const isSelectionMode = useAtomValue($isSelectionMode);
  const searchTerm = useAtomValue($searchTerm);
  const selectSingleFile = useSetAtom($selectSingleFile);
  const toggleFileSelection = useSetAtom($toggleFileSelection);
  const clearSelection = useSetAtom($clearSelection);
  const [currentFolderId, setCurrentFolderId] = useAtom($currentFolderId);
  const uploadFiles = useSetAtom($uploadFilesToCurrentDirectory);
  const [dragCounter, setDragCounter] = React.useState(0);
  const isSearching = !!searchTerm?.trim();

  // Derive isDragging from dragCounter
  const isDragging = dragCounter > 0;

  // Clear selection when folder changes
  React.useEffect(() => {
    clearSelection();
  }, [currentFolderId, clearSelection]);

  // Function to check if a file is a supported document type
  const isSupportedDocument = (file: FileRecord): boolean => {
    // Check by file extension
    const fileExtension = file.name.split('.').pop()?.toLowerCase();

    switch (fileExtension) {
      case 'docx':
      case 'xlsx':
        return true;
    }

    // Also check by MIME type
    switch (file.mimeType) {
      case 'application/vnd.openxmlformats-officedocument.wordprocessingml.document':
      case 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet':
        return true;
      default:
        return false;
    }
  };

  const handleDoubleClick = (file: FileRecord) => {
    if (file.type === 'Directory') {
      setCurrentFolderId(file.id);
    } else if (isSupportedDocument(file)) {
      // Navigate to office editor for supported documents
      navigate(`/office/editor/${file.id}`);
    } else {
      // Do nothing
    }
  };

  const getFileIcon = (file: FileRecord) => {
    if (file.type === 'Directory') {
      return <Folder className="size-12 text-primary/50" />;
    }

    if (file.mimeType.startsWith('image/')) {
      return <Image className="size-12 text-success opacity-50" />;
    }

    if (file.mimeType === 'application/pdf') {
      return <FileText className="size-12 text-failure/50" />;
    }

    return <File className="size-12 text-neutral opacity-50" />;
  };

  const handleDrop = async (
    e: Parameters<
      NonNullable<React.ComponentProps<typeof DropZone>['onDrop']>
    >[0],
  ) => {
    // Reset drag state
    setDragCounter(0);

    const fileItems = e.items.filter((item) => isFileDropItem(item));
    if (!fileItems.length) return;

    const filePromises = fileItems.map((item) => item.getFile());
    const uploadedFiles = await Promise.all(filePromises);

    // Upload files using the bulk upload function
    const results = await uploadFiles(uploadedFiles);

    // Show success and error messages
    results.successful.forEach(({ file }) => {
      toast.success(`Uploaded ${file.name}`);
    });

    results.failed.forEach(({ file, error }) => {
      toast.error(`Failed to upload ${file.name}: ${error}`);
    });
  };

  const handleDragEnter = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();

    // Check if the drag event contains files
    if (e.dataTransfer.items && e.dataTransfer.items.length > 0) {
      setDragCounter((prev) => {
        const newCounter = prev + 1;
        return newCounter;
      });
    }
  };

  const handleDragLeave = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();

    // Check if we're actually leaving the container
    const container = e.currentTarget as HTMLElement;
    const relatedTarget = e.relatedTarget as HTMLElement;

    // Only decrement if we're leaving to an element outside the container
    if (!relatedTarget || !container.contains(relatedTarget)) {
      setDragCounter((prev) => {
        const newCounter = prev - 1;
        return Math.max(0, newCounter);
      });
    }
  };

  const handleDragOver = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();

    // Check if the drag event contains files and ensure isDragging stays true
    if (e.dataTransfer.items && e.dataTransfer.items.length > 0) {
      e.dataTransfer.dropEffect = 'copy';
    }
  };

  const handleContainerDrop = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();

    // Reset drag state when drop happens
    setDragCounter(0);
  };

  const renderEmptyContainer = () => (
    <div className="flex size-full flex-col items-center justify-center">
      <PackageOpen className="text-primary" size={48} strokeWidth={1} />
      {isSearching ? (
        <div className="mt-4 text-center">
          <span>{t.fileExplorer.empty.noSearchResults}</span>
          <br />
          <span className="text-sm text-neutral/60">
            {t.fileExplorer.empty.searchResultsHint}
          </span>
        </div>
      ) : (
        <div className="mt-4 text-center">
          <span>{t.fileExplorer.empty.noFilesInFolder}</span>
          <br />
          <span className="text-sm text-neutral/60">
            {t.fileExplorer.empty.dragDropHint}
          </span>
        </div>
      )}
    </div>
  );

  return (
    <div
      id="file-grid"
      className="@FileGrid relative size-full"
      onDragEnter={handleDragEnter}
      onDragLeave={handleDragLeave}
      onDragOver={handleDragOver}
      onDrop={handleContainerDrop}
    >
      {files.length === 0 && renderEmptyContainer()}
      <div
        className="grid grid-cols-2 gap-4 select-none sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-6"
        role="grid"
        aria-label="File grid"
      >
        {files.map((file) => (
          <div
            key={file.id}
            role="gridcell"
            aria-selected={selectedFileIds.has(file.id)}
            tabIndex={0}
            className={cn(
              'group relative cursor-pointer rounded-lg border-2 p-4 transition-all hover:shadow-md',
              selectedFileIds.has(file.id)
                ? 'border-primary/50 bg-primary/5'
                : 'border-neutral/20 hover:border-neutral/30',
            )}
            onClick={(e) => {
              if (isSelectionMode) {
                toggleFileSelection(file.id);
              } else if (e.ctrlKey || e.metaKey) {
                toggleFileSelection(file.id);
              } else {
                selectSingleFile(file.id);
              }
            }}
            onDoubleClick={() => handleDoubleClick(file)}
            onKeyDown={(e) => {
              // Handle keyboard navigation
              if (e.key === 'Enter' || e.key === ' ') {
                e.preventDefault();
                if (e.key === 'Enter') {
                  handleDoubleClick(file);
                } else {
                  // Space key toggles selection
                  if (isSelectionMode || e.ctrlKey || e.metaKey) {
                    toggleFileSelection(file.id);
                  } else {
                    selectSingleFile(file.id);
                  }
                }
              }
            }}
          >
            {isSelectionMode && (
              <div
                className="absolute top-2 left-2 z-10 rounded bg-white shadow-sm"
                onClick={(e) => e.stopPropagation()}
              >
                <Checkbox
                  isSelected={selectedFileIds.has(file.id)}
                  onChange={() => toggleFileSelection(file.id)}
                  aria-label={`Select ${file.name}`}
                />
              </div>
            )}
            <div
              className="absolute top-1 right-1 opacity-0 transition-opacity group-hover:opacity-100"
              onClick={(e) => e.stopPropagation()}
            >
              <FileActionMenu file={file} />
            </div>
            <div className="flex flex-col items-center gap-2">
              {getFileIcon(file)}
              <p className="line-clamp-2 text-center text-xs break-all">
                {file.name}
              </p>
              <FileIndicators file={file} />
            </div>
          </div>
        ))}
      </div>

      <div
        className="absolute inset-0 bg-white"
        style={{
          visibility: isDragging ? 'visible' : 'hidden',
          pointerEvents: isDragging ? 'auto' : 'none',
        }}
      >
        <DropZone
          onDrop={handleDrop}
          className="size-full border-2 border-dashed border-primary/40 bg-primary/10"
        >
          <label className="flex cursor-pointer flex-col items-center justify-center p-4">
            <UploadIcon className="mb-2 size-8 text-neutral/40" />
            <p className="text-sm text-neutral/60">
              {t.fileExplorer.empty.dragDropHint}
            </p>
          </label>
        </DropZone>
      </div>
    </div>
  );
};
